import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PaymentService } from '@ribera-web/booking-state-access';
import { catchError, map, of, tap } from 'rxjs';
import { JwtUtil } from '@ribera-web/token-decodifier-service';
import { AuthApiService, AuthServerClockService, SessionService } from '@ribera-web/auth-data-acess';

export enum PaymentState {
  EXPIRED = 'EXPIRED',
  LOGGED = 'LOGGED',
  PAYMENT_TOKEN = 'PAYMENT_TOKEN',
  UNDEFINED = 'UNDEFINED'
}

export const paymentGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const token = localStorage.getItem('accessToken');
  const paymentService = inject(PaymentService);
  const jwtUtil = inject(JwtUtil);
  const clock = inject(AuthServerClockService);
  const authApi = inject(AuthApiService);
  const sessionService = inject(SessionService);

  const paymentToken = route.queryParamMap.get('paymentToken');

  if (token) {
    try {
      const expiration = jwtUtil.getExpirationDate(token);
      const expired = clock.isExpired(expiration);
      if (expired === true) {
        localStorage.removeItem('accessToken');
        return router.parseUrl('/auth/login');
      }
      if (expired === false) {
        paymentService.setTokenData(PaymentState.LOGGED);
        return true;
      }

      // Direct checkout navigation after reload: validate with Security MS.
      return authApi.refreshToken().pipe(
        map(response => {
          const refreshed = response.data?.access_token;
          if (!refreshed || !response.timestamp) throw new Error('Invalid refreshed session');
          clock.synchronize(response.timestamp);
          if (clock.isExpired(jwtUtil.getExpirationDate(refreshed)) !== false) {
            throw new Error('Expired refreshed session');
          }
          localStorage.setItem('accessToken', refreshed);
          sessionService.startSessionTimer(refreshed);
          paymentService.setTokenData(PaymentState.LOGGED);
          return true;
        }),
        catchError(() => {
          localStorage.removeItem('accessToken');
          return of(router.parseUrl('/auth/login'));
        })
      );
    } catch {
      localStorage.removeItem('accessToken');
      return router.parseUrl('/auth/login');
    }
  } else if (paymentToken) {
    return paymentService.validatePaymentToken(paymentToken).pipe(
      tap(data => {
        paymentService.setTokenData(PaymentState.PAYMENT_TOKEN);
      }),
      map(() => true),
      catchError(() => {
        paymentService.setTokenData(PaymentState.EXPIRED);
        return of(true);
      })
    );
  }

  paymentService.setTokenData(PaymentState.UNDEFINED);
  return true;
};
